import React, { useState } from 'react';

import { Cell } from 'react-table';
import { Button, Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { Table } from 'components/UI';
import { TrashcanIcon } from 'assets/icons';
import { Dropdown } from 'components/Forms';
import { useAxios } from 'configs/requests';

import { formatGracePeriodActionTime } from './ActionsTask';

interface IGenerateActionTableProps {
  onAddActions: any;
  currentActions?: ICarePlanActionOnTask[] | undefined;
}

const GenerateActionEngine = ({ onAddActions, currentActions }: IGenerateActionTableProps) => {
  const intl = useIntl();
  const currentActionsWithId =
    currentActions?.map((e) => ({ ...e, customId: Math.random().toString() })) || [];
  const [generateActions, setGenerateActions] = useState(currentActionsWithId);
  const [typeSelected, setTypeSelected] = useState('');
  const [periodValue, setPeriodValue] = useState('1');
  const [periodSelected, setPeriodSelected] = useState('');

  const handleAddGenerateAction = () => {
    if (!periodSelected || !periodValue || !typeSelected) return;

    let gracePeriod_ms = 0;
    switch (periodSelected) {
      case 'hour':
        gracePeriod_ms = Number(periodValue) * 3600000;
        break;
      case 'day':
        gracePeriod_ms = Number(periodValue) * 86400000;
        break;
      case 'week':
        gracePeriod_ms = Number(periodValue) * 604800000;
        break;
      case 'month':
        gracePeriod_ms = Number(periodValue) * 2592000000;
        break;
    }

    const newAction = {
      grace_period_action_id: typeSelected,
      gracePeriod_ms,
      customId: Math.random().toString()
    };

    const existAlready = generateActions.find(
      (action) =>
        action.grace_period_action_id === typeSelected && action.gracePeriod_ms === gracePeriod_ms
    );
    if (existAlready) {
      return;
    }

    const generateActionsWithNew = [...generateActions, newAction];
    setGenerateActions(generateActionsWithNew);
    onAddActions(generateActionsWithNew.map((action) => action));
  };

  const handleDelete = (customId: string) => {
    const generateActionsWithoutNew = generateActions.filter(
      (action) => action.customId !== customId
    );
    setGenerateActions(generateActionsWithoutNew);
    onAddActions(generateActionsWithoutNew.map((action) => action));
  };

  const [{ data }] = useAxios<ICarePlanActionOnTask[]>({
    url: '/clinician/gracePeriodActionCatalog',
    method: 'GET'
  });

  const columns = [
    {
      id: 'action',
      Header: intl.formatMessage({ id: 'generateActionKey' }),
      Cell: ({ row: { original } }: Cell<ICarePlanActionOnTask>) => {
        if (original.grace_period_action_id) {
          return intl.formatMessage({ id: original.grace_period_action_id });
        }
        if (original.onCompletionAction?.ruleTag) {
          return intl.formatMessage({ id: original.onCompletionAction.ruleTag });
        }
      }
    },
    {
      id: 'period',
      Header: intl.formatMessage({ id: 'ifUncompletedKey' }),
      align: 'right',
      Cell: ({ row: { original } }: Cell<ICarePlanActionOnTask>) => {
        return original.gracePeriod_ms
          ? formatGracePeriodActionTime(original.gracePeriod_ms, intl)
          : '';
      }
    },
    {
      id: 'delete',
      align: 'center',
      Cell: ({ row: { original } }: Cell<ICarePlanActionOnTask>) => {
        return (
          <Button
            variant="link"
            onClick={() => handleDelete(original.customId || '')}
            className="mt-2"
          >
            <TrashcanIcon className="icon" />
          </Button>
        );
      }
    }
  ];

  const types: ISelectOption[] =
    data
      ?.filter((type) => !type.grace_period_action_id.includes('+'))
      .map((type) => ({
        label: intl.formatMessage({ id: type.grace_period_action_id }),
        value: type.grace_period_action_id
      }))
      .sort((a, b) => (a.label < b.label ? -1 : 1)) || [];
  const periods: ISelectOption[] = [
    { value: 'hour', label: intl.formatMessage({ id: 'hoursKey' }) },
    { value: 'day', label: intl.formatMessage({ id: 'daysKey' }) },
    { value: 'week', label: intl.formatMessage({ id: 'weeksKey' }) },
    { value: 'month', label: intl.formatMessage({ id: 'monthsKey' }) }
  ];

  return (
    <div className="mt-4" data-test="generate-action-section">
      <div className="mb-2 d-flex align-items-center justify-content-between">
        <div className="w-50">
          <Form.Label>{intl.formatMessage({ id: 'generateActionKey' })}</Form.Label>
          <Dropdown
            dataTestId="generate-action-type-select"
            variant="form"
            options={types}
            value={types.find(({ value }) => value === typeSelected)}
            onChange={({ value }: ISelectOption) => setTypeSelected(value)}
            isSearchable={false}
            containerNoWrap
          />
        </div>
        <div className="ms-2">
          <Form.Label>{intl.formatMessage({ id: 'ifUncompletedKey' })}</Form.Label>
          <div className="d-flex align-items-center">
            <Form.Control
              data-test="generate-action-period-input"
              type="number"
              className="w-30"
              value={periodValue}
              onChange={(e) => Number(e.target.value) > 0 && setPeriodValue(e.target.value)}
            />
            <Dropdown
              dataTestId="generate-action-period-select"
              className="ms-2 w-175"
              variant="form"
              options={periods}
              value={periods.find(({ value }) => value === periodSelected)}
              onChange={({ value }: ISelectOption) => setPeriodSelected(value)}
              isSearchable={false}
            />
            <Button
              data-test="generate-action-add-btn"
              size="sm"
              variant="link"
              className="ms-1 w-30"
              onClick={handleAddGenerateAction}
              disabled={!periodSelected || !typeSelected || !periodValue}
            >
              + {intl.formatMessage({ id: 'add' })}
            </Button>
          </div>
        </div>
      </div>
      {generateActions.length > 0 && (
        <Table
          dataTestIdPrefix="generate-action"
          data={generateActions}
          columns={columns}
          hideMargin
          noDataTxt={intl.formatMessage({ id: 'noDataKey' })}
        />
      )}
    </div>
  );
};

export default GenerateActionEngine;
